"use client"

import { useCallback, useState } from "react"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import { BiomarkerListModal } from "@/components/biomarkers/biomarker-list-modal"
import { track } from "@/lib/analytics"
import { buildBiomarkerSeedMessage } from "@/lib/biomarkers/list-seed"

interface BiomarkerCheckCtaProps {
  /**
   * Seeds a message into the Ella AI drawer. The parent wires this to the
   * biomarkers page's `openAi`, so the pasted list flows through `/api/chat`
   * to the customer-support resolver.
   */
  onCheck: (message: string) => void
}

/** Count the terms a pasted list holds, for the analytics payload only. */
function countTerms(rawList: string): number {
  return rawList
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean).length
}

/**
 * "Already have a biomarker list?" CTA on the biomarkers page. Opens a modal
 * to paste a list, then seeds it into the Ella drawer for an instant coverage
 * answer. Rendered only where the AI assistant is available (parent gate).
 */
export function BiomarkerCheckCta({ onCheck }: BiomarkerCheckCtaProps) {
  const t = useTranslations("biomarkers.listCheck")
  const [open, setOpen] = useState(false)

  const handleClose = useCallback(() => setOpen(false), [])
  const handleSubmit = useCallback(
    (rawList: string) => {
      setOpen(false)
      track.biomarkerListSubmitted(countTerms(rawList))
      onCheck(buildBiomarkerSeedMessage(rawList))
    },
    [onCheck]
  )

  return (
    <>
      <section className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div className="flex flex-col gap-5 rounded-[16px] bg-white p-6 md:flex-row md:items-center md:justify-between md:gap-8 md:p-8">
          <div className="max-w-xl">
            <p className="type-label-md text-bright-blue-700">{t("eyebrow")}</p>
            <h2 className="type-title-lg mt-2 text-neutral-900">
              {t("heading")}
            </h2>
            <p className="type-body-md mt-2 text-neutral-600">{t("body")}</p>
          </div>
          <div className="flex shrink-0 flex-col items-start gap-2 md:items-end">
            <Button
              type="button"
              variant="primary"
              className="bg-deep-blue-950 hover:bg-deep-blue-900"
              onClick={() => setOpen(true)}
            >
              {t("button")}
            </Button>
            <p className="type-body-sm text-neutral-400">{t("microcopy")}</p>
          </div>
        </div>
      </section>
      <BiomarkerListModal
        open={open}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </>
  )
}
